/**
 * Format an E.164 phone number for display under a contact's name.
 *
 * Only Malaysian mobile numbers (`+60`) are given a grouped format —
 * `+60199933996` -> `+60 19-9933 996`. Any other country code (or a `+60`
 * number whose national part doesn't match the expected mobile length) is
 * returned unchanged rather than guessed at.
 *
 * Mirrors the single-purpose style of `format-time.ts`: one function, no
 * external date/phone library, graceful on null/empty input.
 */

const MY_COUNTRY_CODE = '+60';

/**
 * Digit-group sizes for a Malaysian mobile national number (the part after
 * `+60`). `[2, 4, 3]` turns `"199933996"` into `["19", "9933", "996"]`,
 * joined as `19-9933 996`. Kept as one small constant so the grouping is
 * tunable without touching the parsing logic below.
 */
const MY_MOBILE_GROUPS = [2, 4, 3] as const;

export function formatPhone(e164: string | null | undefined): string {
  if (!e164) {
    return '';
  }
  if (!e164.startsWith(MY_COUNTRY_CODE)) {
    return e164;
  }

  const national = e164.slice(MY_COUNTRY_CODE.length);
  const expectedLength = MY_MOBILE_GROUPS.reduce((sum, n) => sum + n, 0);
  if (national.length !== expectedLength || !/^\d+$/.test(national)) {
    // Doesn't match the expected mobile shape (e.g. a landline, or a number
    // with an unusual length) — return unchanged rather than mis-group it.
    return e164;
  }

  const groups: string[] = [];
  let offset = 0;
  for (const size of MY_MOBILE_GROUPS) {
    groups.push(national.slice(offset, offset + size));
    offset += size;
  }
  const [prefix, mid, tail] = groups;
  return `${MY_COUNTRY_CODE} ${prefix}-${mid} ${tail}`;
}
